import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { calculateAbilityLevelCap, splitAbilityXP } from '../utils/levelCalculation';
import { validateDependencies } from '../utils/dependencyValidation';
import type { Ability, Skill, SkillRequirement, ColorVariationMethod, AbilityFolder, CategoryStats, MediaItem } from '../types';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount, swappingAccount,
} from '../lib/accountStorage';
import {
  createBlobSync, mergeCollection, mergeTombstones, pruneTombstones, tombstone, sameRecords,
  matchesCloud, sameTombstones,
  type Tombstones,
} from '../lib/syncBlob';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';
import { useActivityLogStore } from './activityLogStore';
import { rollupSkill, setRequirementCompleted, type AwardContext } from '../utils/skillProgress';
import { convertLegacyAbilities } from '../utils/abilityProgressMigration';

// ─── Supabase sync (sync epic) ──────────────────────────────────────────────
// Abilities + folders sync as ONE atomic blob per user (user_abilities.data).
// Whole-store-blob keeps the deeply nested data (ability → skills →
// requirements) and the ability↔folder refs consistent. A single store
// subscription auto-syncs on any abilities/folders change; ref-equality skips
// non-data changes (incl. derived cap recalcs, which only return a new abilities
// ref on a genuine change).
// `domainLevels` is derived → not synced.

const USER_ABILITIES_TABLE = 'user_abilities';
function readTombstones(data: any): Tombstones {
  const raw = data?.deleted;
  return raw && typeof raw === 'object' ? (raw as Tombstones) : {};
}

// ⚠️ Read-modify-write, NOT a wholesale upsert. The old code PUT the entire local
// {abilities, folders}, so a device holding a stale cache silently deleted records
// it had never seen (diagnosed 2026-07-27 — see lib/syncBlob.ts). Ability and folder
// ids are both UUIDs, so ONE tombstone map covers both.
/**
 * The ability's next updatedAt: now, but always after the stamp it replaces. The
 * blob merge keeps the cloud copy on a tie and `matchesCloud` compares
 * id@updatedAt, so a change that doesn't move this forward is never written and
 * the next load restores the old copy (see questStore's touchQuest).
 */
export function touchAbility(ability: { updatedAt?: string }): string {
  const previous = ability.updatedAt ? Date.parse(ability.updatedAt) : Number.NaN;
  const now = Date.now();
  return new Date(Number.isFinite(previous) && previous >= now ? previous + 1 : now).toISOString();
}

const conversionContext = (userLevel: number | undefined) => ({
  now: new Date().toISOString(), userLevel: userLevel || 1, touch: touchAbility,
});

const abilitySync = createBlobSync({
  transport: supabaseBlobTransport(USER_ABILITIES_TABLE),
  owner: () => useAbilitiesStore.getState().cachedUserId,
  onError: err => console.error('Failed to sync abilities to Supabase:', err),
  build: (cloud) => {
    const { abilities, folders, deleted } = useAbilitiesStore.getState();
    const cloudTombs = readTombstones(cloud);
    const merged = pruneTombstones(mergeTombstones(deleted, cloudTombs));
    const arr = (v: any) => (Array.isArray(v) ? v : []);
    // Rows written by an older build (another device, an old open tab) may still
    // lack their XP records: convert before comparing, so the cloud converges too.
    const nextAbilities = convertLegacyAbilities(
      mergeCollection(abilities, arr(cloud?.abilities), merged),
      conversionContext(useAbilitiesStore.getState().userLevel),
    );
    const nextFolders = mergeCollection(folders, arr(cloud?.folders), merged);
    if (matchesCloud([[nextAbilities, cloud?.abilities], [nextFolders, cloud?.folders]], merged, cloudTombs)) return null;
    return { abilities: nextAbilities, folders: nextFolders, deleted: merged };
  },
});

interface AbilitiesState {
  abilities: Ability[];
  folders: AbilityFolder[];
  /** id → deletedAt (abilities AND folders). Stops the union-on-write
   *  resurrecting anything deleted — including the deduped default copies. */
  deleted: Tombstones;
  userLevel: number;
  domainLevels: Record<string, number>;
  initializedUserId: string | null;
  cachedUserId: string | null;
  initialize: () => Promise<void>;

  // Folder operations
  addFolder: (folder: Omit<AbilityFolder, 'id' | 'createdAt' | 'updatedAt' | 'order'>) => string;
  updateFolder: (id: string, updates: Partial<AbilityFolder>) => void;
  deleteFolder: (id: string, moveAbilitiesTo?: string | null) => void;
  reorderFolders: (folderId: string, newOrder: number) => void;
  toggleFolderCollapsed: (id: string) => void;

  // Ability operations
  addAbility: (ability: Omit<Ability, 'id' | 'createdAt' | 'updatedAt' | 'level' | 'xp' | 'progress' | 'order'>) => string;
  updateAbility: (id: string, updates: Partial<Ability>) => void;
  updateAbilityColorMethod: (id: string, colorVariationMethod: ColorVariationMethod) => void;
  deleteAbility: (id: string) => void;
  moveAbilityToFolder: (abilityId: string, folderId: string | null) => void;
  reorderAbilities: (abilityId: string, newOrder: number, folderId?: string | null) => void;

  // Skill operations
  addSkill: (abilityId: string, skill: Omit<Skill, 'id' | 'createdAt' | 'updatedAt' | 'xp' | 'progress'>) => string;
  updateSkill: (abilityId: string, skillId: string, updates: Partial<Skill>) => void;
  deleteSkill: (abilityId: string, skillId: string) => void;

  // Requirement operations
  addRequirement: (abilityId: string, skillId: string, requirement: Omit<SkillRequirement, 'id' | 'createdAt' | 'updatedAt' | 'completed'>) => string;
  updateRequirement: (abilityId: string, skillId: string, requirementId: string, updates: Partial<SkillRequirement>) => void;
  deleteRequirement: (abilityId: string, skillId: string, requirementId: string) => void;
  toggleRequirement: (abilityId: string, skillId: string, requirementId: string) => void;
  updateRequirementProgress: (abilityId: string, skillId: string, requirementId: string, progress: number) => void;
  submitRequirementOutput: (abilityId: string, skillId: string, requirementId: string, outputContent: string, outputFile?: MediaItem) => void;

  // Domain level management
  updateDomainLevels: (categories: CategoryStats[]) => void;
  recalculateAbilityCaps: () => void;

  // Getters
  getAbilityById: (id: string) => Ability | undefined;
  getSkillById: (abilityId: string, skillId: string) => Skill | undefined;
  getRequirementById: (abilityId: string, skillId: string, requirementId: string) => SkillRequirement | undefined;

  // User level
  setUserLevel: (level: number) => void;
}

// Default abilities
const DEFAULT_ABILITIES: Omit<Ability, 'id' | 'createdAt' | 'updatedAt' | 'level' | 'xp' | 'progress'>[] = [
  {
    name: 'Public Speaking',
    description: 'Master the art of effective communication and presentation',
    category: 'social',
    icon: 'Mic',
    skills: [],
    order: 0
  },
  {
    name: 'Programming',
    description: 'Develop software engineering and coding skills',
    category: 'intellectual',
    icon: 'Code',
    skills: [],
    order: 1
  },
  {
    name: 'Fitness',
    description: 'Build strength, endurance, and overall physical health',
    category: 'personal',
    icon: 'Dumbbell',
    skills: [],
    order: 2
  }
];

// A stable, deterministic id per default ability. Using a fixed id (instead of a
// fresh crypto.randomUUID() on every seed) makes seeding IDEMPOTENT: even if the
// defaults get seeded again on another device / a cleared browser, the id-based
// merge collapses them instead of accumulating copies. This is
// what fixes the "Public Speaking / Programming / Fitness duplicated N times" bug.
function defaultAbilityId(category: string, name: string): string {
  return `default-ability-${category}-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

// Build the seed abilities with stable ids + full default fields (mirrors what
// addAbility produces, minus the random id).
function buildDefaultAbilities(domainLevels: Record<string, number>): Ability[] {
  const now = new Date().toISOString();
  return DEFAULT_ABILITIES.map((d, index) => ({
    ...d,
    id: defaultAbilityId(d.category, d.name),
    order: d.order ?? index,
    createdAt: now,
    updatedAt: now,
    level: 1,
    xp: 0,
    progress: 0,
    excessXP: 0,
    totalXPEarned: 0,
    isCapped: false,
    lastDomainLevelCheck: domainLevels[d.category] || 0,
    skills: [],
    colorVariationMethod: 'tint-shade' as ColorVariationMethod,
  }));
}

const DEFAULT_SIGNATURES = new Set(DEFAULT_ABILITIES.map((d) => `${d.name}::${d.category}`));

// Collapse the duplicate empty default abilities left behind by the old
// random-id seeding bug: keep ONE untouched default per name+category and drop
// the rest. Only targets pristine defaults (matching name+category, no skills,
// no XP) — user-created abilities and any default the user has actually worked on
// (skills added / XP earned) are left completely alone.
function dedupeEmptyDefaults(abilities: Ability[]): Ability[] {
  const seen = new Set<string>();
  const result: Ability[] = [];
  for (const a of abilities) {
    const key = `${a.name}::${a.category}`;
    const isPristineDefault =
      DEFAULT_SIGNATURES.has(key) &&
      (a.skills?.length ?? 0) === 0 &&
      (a.totalXPEarned ?? 0) === 0 &&
      (a.xp ?? 0) === 0;
    if (isPristineDefault) {
      if (seen.has(key)) continue; // drop the duplicate empty default
      seen.add(key);
    }
    result.push(a);
  }
  return result;
}

// Helper function to calculate ability progress with dynamic domain-based cap
function calculateAbilityProgress(
  skills: Skill[],
  domainLevel: number
): {
  progress: number;
  xp: number;
  level: number;
  excessXP: number;
  totalXPEarned: number;
  isCapped: boolean;
} {
  if (skills.length === 0) {
    return { progress: 0, xp: 0, level: 1, excessXP: 0, totalXPEarned: 0, isCapped: false };
  }

  // Calculate total XP earned from skills
  const totalXPEarned = skills.reduce((sum, skill) => sum + skill.xp, 0);

  // Calculate level cap based on domain level
  const levelCap = calculateAbilityLevelCap(domainLevel);

  // Split XP between applied and excess
  const { appliedXP, excessXP } = splitAbilityXP(totalXPEarned, domainLevel);

  // XP thresholds for levels 1-10 (base progression)
  const baseXpThresholds = [
    0,      // Level 1 (start)
    150,    // Level 2
    350,    // Level 3
    600,    // Level 4
    950,    // Level 5
    1400,   // Level 6
    2000,   // Level 7
    2750,   // Level 8
    3700,   // Level 9
    5000    // Level 10
  ];

  // Build extended thresholds array up to level cap
  const xpThresholds = [...baseXpThresholds];
  if (levelCap > 10) {
    const growthRate = 1.4;
    for (let i = 10; i < levelCap; i++) {
      const xpForNextLevel = Math.floor(baseXpThresholds[9] * Math.pow(growthRate, i - 9));
      xpThresholds.push(xpThresholds[i - 1] + xpForNextLevel);
    }
  }

  // Determine current level based on applied XP
  let level = 1;
  for (let i = 0; i < Math.min(xpThresholds.length, levelCap); i++) {
    if (appliedXP >= xpThresholds[i]) {
      level = i + 1;
    } else {
      break;
    }
  }
  level = Math.min(levelCap, level);

  // Check if ability is at cap
  const isCapped = level >= levelCap;

  // Calculate progress to next level
  let progress = 0;
  if (level < levelCap && level < xpThresholds.length) {
    const xpForCurrentLevel = xpThresholds[level - 1];
    const xpForNextLevel = xpThresholds[level];
    progress = Math.round(((appliedXP - xpForCurrentLevel) / (xpForNextLevel - xpForCurrentLevel)) * 100);
  } else {
    // At max level (capped), show 100% progress
    progress = 100;
  }

  return { progress, xp: appliedXP, level, excessXP, totalXPEarned, isCapped };
}

// ─── The journal (activity log) ─────────────────────────────────────────────
/**
 * ⭐⭐ WHAT GOT PAID, DIFFED — the same rule projectStore uses, for the same
 * reason. `activityType: 'skill'` shipped with a render branch and a filter
 * option and ZERO writers, so "Skills" in Reflection → Activity was empty
 * forever. THREE actions can complete a requirement (toggling it, dragging its
 * progress to 100%, submitting its output), so a per-click write would have
 * left the same hole in whichever one got forgotten.
 *
 * ⚠️ A requirement counts as PAID when it carries a `pointRecord` — the same
 * value `useUserStats` sums. The record is created once and kept on un-complete
 * (RO-A, owner decision RO-D1), so completing again writes no second row.
 */
type PaidReqKey = string;

function paidRequirementKeys(abilities: Ability[]): Set<PaidReqKey> {
  const keys = new Set<PaidReqKey>();
  for (const a of abilities) {
    for (const s of a.skills ?? []) {
      for (const r of s.requirements ?? []) {
        if (r.pointRecord) keys.add(`${a.id}:${s.id}:${r.id}`);
      }
    }
  }
  return keys;
}

function logPaidRequirements(before: Set<PaidReqKey>, abilities: Ability[]): void {
  // ⚠️ `getState()` at call time — the log store hydrates asynchronously.
  const add = useActivityLogStore.getState().addActivity;
  const at = new Date().toISOString();
  for (const a of abilities) {
    for (const s of a.skills ?? []) {
      for (const r of s.requirements ?? []) {
        const key = `${a.id}:${s.id}:${r.id}`;
        if (!r.pointRecord || before.has(key)) continue;
        void add({
          activityType: 'skill',
          activityName: r.title,
          domainId: a.category || '',
          xpEarned: r.pointRecord.points,
          metadata: { abilityId: a.id, abilityName: a.name, skillId: s.id, skillName: s.name, requirementId: r.id },
          completedAt: at,
        });
      }
    }
  }
}

/**
 * `set`, plus the journal. Same call shape as `set`, so an action opts in by
 * changing one word and its body stays untouched.
 */
function setAndLogSkillXp(
  set: (updater: (state: AbilitiesState) => Partial<AbilitiesState>) => void,
  get: () => AbilitiesState,
  updater: (state: AbilitiesState) => Partial<AbilitiesState>,
): void {
  const before = paidRequirementKeys(get().abilities);
  set(updater);
  logPaidRequirements(before, get().abilities);
}

/** Recompute an ability's derived numbers from its skills and stamp it. */
function withSkills(ability: Ability, skills: Skill[], state: AbilitiesState): Ability {
  const domainLevel = state.domainLevels[ability.category] || 0;
  const { progress, xp, level, excessXP, totalXPEarned, isCapped } = calculateAbilityProgress(skills, domainLevel);
  return { ...ability, skills, progress, xp, level, excessXP, totalXPEarned, isCapped, lastDomainLevelCheck: domainLevel, updatedAt: touchAbility(ability) };
}

/** `list.map(fn)`, but the SAME array when nothing changed. */
function mapSame<T>(list: T[], fn: (item: T) => T): T[] {
  let changed = false;
  const next = list.map(item => { const value = fn(item); if (value !== item) changed = true; return value; });
  return changed ? next : list;
}

/**
 * The one path every requirement action takes: edit the skill's requirements,
 * roll the skill up (completion date, bonus once), recompute and stamp the
 * ability. An edit that returns the same array is a no-op (no stamp, no write).
 */
function editRequirements(
  state: AbilitiesState,
  abilityId: string,
  skillId: string,
  edit: (requirements: SkillRequirement[], ctx: AwardContext) => SkillRequirement[],
  options: { payBonus?: boolean } = {},
): Partial<AbilitiesState> {
  const ctx: AwardContext = { now: new Date().toISOString(), userLevel: state.userLevel, payBonus: options.payBonus ?? true };
  let changed = false;
  const abilities = state.abilities.map(ability => {
    if (ability.id !== abilityId) return ability;
    const skills = ability.skills.map(skill => {
      if (skill.id !== skillId) return skill;
      const requirements = edit(skill.requirements, ctx);
      if (requirements === skill.requirements) return skill;
      changed = true;
      return { ...rollupSkill(skill, requirements, ctx), updatedAt: ctx.now };
    });
    return changed ? withSkills(ability, skills, state) : ability;
  });
  return changed ? { abilities } : {};
}

const isLinked = (req: SkillRequirement) => !!(req.linkType && req.linkId);

export const useAbilitiesStore = create<AbilitiesState>()(
  persist<AbilitiesState, [], [], any>(
    (set, get) => ({
      abilities: [],
      folders: [],
      deleted: {},
      userLevel: 1,
      domainLevels: {},
      initializedUserId: null,
      cachedUserId: null,

      // Load-on-login from the user_abilities blob, keyed by user id (re-runs on
      // account switch). Cloud-wins (atomic snapshot replace) when present; else
      // keep this user's local data (or reset on a different account). Guarded so
      // the store subscription doesn't echo the load back to cloud.
      initialize: () => dedupeInit('user_abilities', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          // Cache-first boot: while this user's local cache is fresh, skip the
          // cloud read entirely (write-through keeps cloud current meanwhile).
          if (get().cachedUserId === user.id && isCloudFresh('user_abilities', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }

          const { data, error } = await supabase
            .from(USER_ABILITIES_TABLE)
            .select('*');
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const row = (data ?? [])[0];

          set((state) => {
            const cacheIsOurs =
              state.cachedUserId === null || state.cachedUserId === user.id;

            if (row && row.data && Array.isArray(row.data.abilities)) {
              // Existing user: merge cloud + local, then collapse the duplicate
              // empty defaults the old seeding bug accumulated.
              const tombs = pruneTombstones(mergeTombstones(
                cacheIsOurs ? state.deleted : {}, readTombstones(row.data)));
              const merged = mergeCollection(cacheIsOurs ? state.abilities : [], row.data.abilities, tombs);
              // RO-A: a cache-free device converts legacy rows here (records only).
              const deduped = convertLegacyAbilities(dedupeEmptyDefaults(merged), conversionContext(state.userLevel));
              // ⚠️ The dedupe DROPS records. Writes are a union now, so each
              // dropped copy needs a real tombstone or it comes straight back —
              // which is exactly how the old seeding bug accumulated duplicates
              // in the cloud blob in the first place.
              const kept = new Set(deduped.map(a => a.id));
              let deleted = tombs;
              for (const a of merged) if (!kept.has(a.id)) deleted = tombstone(deleted, a.id);
              const folders = mergeCollection(
                cacheIsOurs ? state.folders : [],
                Array.isArray(row.data.folders) ? row.data.folders : [],
                deleted,
              );
              return {
                // Reuse the previous reference on a no-op merge so the
                // subscription doesn't fire a pointless write every boot.
                abilities: sameRecords(deduped, state.abilities) ? state.abilities : deduped,
                folders: sameRecords(folders, state.folders) ? state.folders : folders,
                deleted: sameTombstones(deleted, state.deleted) ? state.deleted : deleted,
                initializedUserId: user.id,
                cachedUserId: user.id,
              };
            }

            // No cloud row → brand-new account. Seed the example defaults ONCE,
            // with stable ids (so this can never duplicate on a future load).
            const base = cacheIsOurs ? state.abilities : [];
            const folders = cacheIsOurs ? state.folders : [];
            let deleted = cacheIsOurs ? state.deleted : {};
            let abilities: Ability[];
            if (base.length === 0) {
              abilities = buildDefaultAbilities(state.domainLevels);
            } else {
              abilities = convertLegacyAbilities(dedupeEmptyDefaults(base), conversionContext(state.userLevel));
              const kept = new Set(abilities.map(a => a.id));
              for (const a of base) if (!kept.has(a.id)) deleted = tombstone(deleted, a.id);
            }
            return {
              abilities: sameRecords(abilities, state.abilities) ? state.abilities : abilities,
              folders,
              deleted,
              initializedUserId: user.id,
              cachedUserId: user.id,
            };
          });
          // The seed, the dedupe — or records the row lacks: written when the cloud lacks them.
          abilitySync.writeBack(row?.data);
          markCloudFresh('user_abilities', user.id);
        } catch (error) {
          console.error('Failed to initialize abilities from Supabase:', error);
        }
      }),

      setUserLevel: (level) => set({ userLevel: level }),

      updateDomainLevels: (categories: CategoryStats[]) => {
        const newDomainLevels: Record<string, number> = {};
        categories.forEach(cat => {
          newDomainLevels[cat.id] = cat.totalLevel || 0;
        });

        const currentDomainLevels = get().domainLevels;

        // Check if domain levels have actually changed
        const hasChanges = Object.keys(newDomainLevels).some(
          key => currentDomainLevels[key] !== newDomainLevels[key]
        );

        if (!hasChanges) {
          return;
        }

        set({ domainLevels: newDomainLevels });

        // Recalculate ability caps when domain levels change
        get().recalculateAbilityCaps();
      },

      recalculateAbilityCaps: () => {
        set((state) => {
          let hasChanges = false;
          const updatedAbilities = state.abilities.map(ability => {
            const domainLevel = state.domainLevels[ability.category] || 0;

            // Only recalculate if domain level has changed
            if (ability.lastDomainLevelCheck === domainLevel) {
              return ability;
            }

            hasChanges = true;

            // Recalculate with new domain level
            const { progress, xp, level, excessXP, totalXPEarned, isCapped } = calculateAbilityProgress(
              ability.skills,
              domainLevel
            );

            return {
              ...ability,
              level,
              xp,
              progress,
              excessXP,
              totalXPEarned,
              isCapped,
              lastDomainLevelCheck: domainLevel,
              updatedAt: touchAbility(ability)
            };
          });

          // Only update state if there were actual changes
          if (hasChanges) {
            return { abilities: updatedAbilities };
          }
          return state;
        });
      },

      // Folder operations
      addFolder: (folderData) => {
        const id = crypto.randomUUID();
        const state = get();
        const maxOrder = state.folders.reduce((max, f) => Math.max(max, f.order), -1);

        const folder: AbilityFolder = {
          ...folderData,
          id,
          order: maxOrder + 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        set((state) => ({
          folders: [...state.folders, folder]
        }));

        return id;
      },

      updateFolder: (id, updates) => {
        set((state) => ({
          folders: state.folders.map((folder) =>
            folder.id === id
              ? {
                  ...folder,
                  ...updates,
                  updatedAt: touchAbility(folder)
                }
              : folder
          )
        }));
      },

      deleteFolder: (id, moveAbilitiesTo) => {
        set((state) => {
          // Move or remove abilities from this folder
          const updatedAbilities = state.abilities.map(ability => {
            if (ability.folderId === id) {
              return {
                ...ability,
                folderId: moveAbilitiesTo === null ? undefined : moveAbilitiesTo,
                updatedAt: touchAbility(ability)
              };
            }
            return ability;
          });

          return {
            folders: state.folders.filter(folder => folder.id !== id),
            abilities: updatedAbilities,
            // ⚠️ Tombstone required — writes are a union now.
            deleted: tombstone(state.deleted, id),
          };
        });
      },

      reorderFolders: (folderId, newOrder) => {
        set((state) => {
          const folder = state.folders.find(f => f.id === folderId);
          if (!folder) return state;

          const oldOrder = folder.order;
          const updatedFolders = state.folders.map(f => {
            if (f.id === folderId) {
              return { ...f, order: newOrder, updatedAt: touchAbility(f) };
            }
            // Shifted siblings changed too (same merge rule as abilities).
            if (oldOrder < newOrder && f.order > oldOrder && f.order <= newOrder) {
              return { ...f, order: f.order - 1, updatedAt: touchAbility(f) };
            }
            if (oldOrder > newOrder && f.order >= newOrder && f.order < oldOrder) {
              return { ...f, order: f.order + 1, updatedAt: touchAbility(f) };
            }
            return f;
          }).sort((a, b) => a.order - b.order);

          return { folders: updatedFolders };
        });
      },

      toggleFolderCollapsed: (id) => {
        set((state) => ({
          folders: state.folders.map(folder =>
            folder.id === id
              ? { ...folder, collapsed: !folder.collapsed, updatedAt: touchAbility(folder) }
              : folder
          )
        }));
      },

      // Ability operations
      addAbility: (abilityData) => {
        const id = crypto.randomUUID();
        const state = get();

        // Calculate order based on folder or overall
        let maxOrder = -1;
        if (abilityData.folderId) {
          maxOrder = state.abilities
            .filter(a => a.folderId === abilityData.folderId)
            .reduce((max, a) => Math.max(max, a.order), -1);
        } else {
          maxOrder = state.abilities
            .filter(a => !a.folderId)
            .reduce((max, a) => Math.max(max, a.order), -1);
        }

        const domainLevel = state.domainLevels[abilityData.category] || 0;

        const ability: Ability = {
          ...abilityData,
          id,
          order: maxOrder + 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          level: 1,
          xp: 0,
          progress: 0,
          excessXP: 0,
          totalXPEarned: 0,
          isCapped: false,
          lastDomainLevelCheck: domainLevel,
          skills: [],
          color: abilityData.color,
          colorVariationMethod: abilityData.colorVariationMethod || 'tint-shade'
        };

        set((state) => ({
          abilities: [...state.abilities, ability]
        }));

        return id;
      },
      
      updateAbility: (id, updates) => {
        set((state) => ({
          abilities: state.abilities.map((ability) =>
            ability.id === id
              ? {
                  ...ability,
                  ...updates,
                  updatedAt: touchAbility(ability)
                }
              : ability
          )
        }));
      },

      updateAbilityColorMethod: (id, colorVariationMethod) => {
        set((state) => ({
          abilities: state.abilities.map((ability) =>
            ability.id === id
              ? {
                  ...ability,
                  colorVariationMethod,
                  updatedAt: touchAbility(ability)
                }
              : ability
          )
        }));
      },

      deleteAbility: (id) => {
        // ⚠️ Needs a tombstone — writes are a union now, so a bare filter would
        // be undone by the next merge against the cloud copy.
        set((state) => ({
          abilities: state.abilities.filter((ability) => ability.id !== id),
          deleted: tombstone(state.deleted, id),
        }));
      },

      moveAbilityToFolder: (abilityId, folderId) => {
        set((state) => {
          const ability = state.abilities.find(a => a.id === abilityId);
          if (!ability) return state;

          // Calculate new order in target location
          let maxOrder = -1;
          if (folderId) {
            maxOrder = state.abilities
              .filter(a => a.folderId === folderId)
              .reduce((max, a) => Math.max(max, a.order), -1);
          } else {
            maxOrder = state.abilities
              .filter(a => !a.folderId)
              .reduce((max, a) => Math.max(max, a.order), -1);
          }

          return {
            abilities: state.abilities.map(a =>
              a.id === abilityId
                ? {
                    ...a,
                    folderId: folderId || undefined,
                    order: maxOrder + 1,
                    updatedAt: touchAbility(a)
                  }
                : a
            )
          };
        });
      },

      reorderAbilities: (abilityId, newOrder, folderId) => {
        set((state) => {
          const ability = state.abilities.find(a => a.id === abilityId);
          if (!ability) return state;

          const oldOrder = ability.order;
          const targetFolderId = folderId !== undefined ? folderId : ability.folderId;

          const updatedAbilities = state.abilities.map(a => {
            // Only affect abilities in the same folder/context
            const inSameContext = (targetFolderId ? a.folderId === targetFolderId : !a.folderId);

            if (!inSameContext) return a;

            if (a.id === abilityId) {
              return { ...a, order: newOrder, updatedAt: touchAbility(a) };
            }

            // A shifted sibling changed too: stamp it, or its new order never syncs.
            if (oldOrder < newOrder && a.order > oldOrder && a.order <= newOrder) {
              return { ...a, order: a.order - 1, updatedAt: touchAbility(a) };
            }
            if (oldOrder > newOrder && a.order >= newOrder && a.order < oldOrder) {
              return { ...a, order: a.order + 1, updatedAt: touchAbility(a) };
            }
            return a;
          });

          return { abilities: updatedAbilities };
        });
      },

      // Skill operations
      addSkill: (abilityId, skillData) => {
        const state = get();
        const ability = state.abilities.find(a => a.id === abilityId);

        if (!ability) {
          console.error('Ability not found:', abilityId);
          return '';
        }

        // Validate dependencies if provided
        if (skillData.dependsOn && skillData.dependsOn.length > 0) {
          const id = crypto.randomUUID();
          const validation = validateDependencies(id, skillData.dependsOn, ability.skills);

          if (!validation.valid) {
            console.error('Invalid skill dependencies:', validation.error);
            throw new Error(validation.error);
          }
        }

        const id = crypto.randomUUID();
        const skill: Skill = {
          ...skillData,
          id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          xp: 0,
          progress: 0,
          requirements: []
        };

        set((state) => {
          const updatedAbilities = state.abilities.map(ability => {
            if (ability.id !== abilityId) return ability;

            return {
              ...ability,
              skills: [...ability.skills, skill],
              updatedAt: touchAbility(ability)
            };
          });

          return { abilities: updatedAbilities };
        });

        return id;
      },
      
      updateSkill: (abilityId, skillId, updates) => {
        const state = get();
        const ability = state.abilities.find(a => a.id === abilityId);

        if (!ability) {
          console.error('Ability not found:', abilityId);
          return;
        }

        // Validate dependencies if being updated
        if (updates.dependsOn !== undefined) {
          const validation = validateDependencies(skillId, updates.dependsOn || [], ability.skills);

          if (!validation.valid) {
            console.error('Invalid skill dependencies:', validation.error);
            throw new Error(validation.error);
          }
        }

        set((state) => {
          const updatedAbilities = state.abilities.map(ability => {
            if (ability.id !== abilityId) return ability;

            // Earned state isn't editable here: resetting the bonus flag/record would
            // let the same skill pay its bonus twice (review finding 4).
            const { bonusRecord: _bonusRecord, completionBonusAwarded: _awarded, completedAt: _completedAt, xp: _xp, ...editable } = updates as Partial<Skill>;
            void _bonusRecord; void _awarded; void _completedAt; void _xp;
            const updatedSkills = ability.skills.map(skill =>
              skill.id === skillId
                ? {
                    ...skill,
                    ...editable,
                    updatedAt: new Date().toISOString()
                  }
                : skill
            );

            return {
              ...ability,
              skills: updatedSkills,
              updatedAt: touchAbility(ability)
            };
          });

          return { abilities: updatedAbilities };
        });
      },
      
      deleteSkill: (abilityId, skillId) => {
        set((state) => {
          const updatedAbilities = state.abilities.map(ability => {
            if (ability.id !== abilityId) return ability;
            
            // Recompute: the deleted skill's XP leaves the ability's level now, not later.
            return withSkills(ability, ability.skills.filter(skill => skill.id !== skillId), state);
          });
          
          return { abilities: updatedAbilities };
        });
      },
      
      // Requirement operations
      addRequirement: (abilityId, skillId, requirementData) => {
        const id = crypto.randomUUID();
        const now = new Date().toISOString();
        const { pointRecord: _pointRecord, completedAt: _completedAt, ...data } = requirementData as SkillRequirement;
        void _pointRecord; void _completedAt;
        const requirement: SkillRequirement = {
          ...data,
          id,
          createdAt: now,
          updatedAt: now,
          completed: false,
          xp: 0,
          requirementType: requirementData.requirementType || 'input', // Default to input type
          progress: 0
        };
        // A new requirement is unfinished: a done skill stops being done (its bonus stays).
        set((state) => editRequirements(state, abilityId, skillId, requirements => [...requirements, requirement]));
        return id;
      },

      // Metadata edits (title, link, learn-mode access). Never pays; an explicit
      // `completed` here is non-paying bookkeeping, and records can't be edited.
      updateRequirement: (abilityId, skillId, requirementId, updates) => {
        set((state) => editRequirements(state, abilityId, skillId, (requirements, ctx) => mapSame(requirements, req => {
          if (req.id !== requirementId) return req;
          const { pointRecord: _pointRecord, completedAt: _completedAt, xp: _xp, ...rest } = updates as Partial<SkillRequirement>;
          void _pointRecord; void _completedAt; void _xp;
          const merged: SkillRequirement = { ...req, ...rest, updatedAt: ctx.now, xp: req.pointRecord?.points ?? 0 };
          if (rest.completed === undefined || rest.completed === req.completed) return merged;
          const progress = rest.progress ?? (rest.completed ? 100 : isLinked(req) ? Math.min(99, req.progress) : 0);
          return setRequirementCompleted(merged, rest.completed, ctx, { pay: false, progress });
        }), { payBonus: false }));
      },

      // Deleting a paid requirement removes its record (a private delete); the skill keeps its bonus.
      deleteRequirement: (abilityId, skillId, requirementId) => {
        set((state) => editRequirements(state, abilityId, skillId, requirements => {
          const next = requirements.filter(req => req.id !== requirementId);
          return next.length === requirements.length ? requirements : next;
        }));
      },

      toggleRequirement: (abilityId, skillId, requirementId) => {
        setAndLogSkillXp(set, get, (state) => editRequirements(state, abilityId, skillId, (requirements, ctx) => mapSame(requirements, req => {
          if (req.id !== requirementId) return req;
          // An output requirement can't be completed without its output.
          if (req.requirementType === 'output' && !req.outputContent && !req.outputFile) return req;
          // Pays only the first time; un-completing keeps the record (RO-D1).
          return { ...setRequirementCompleted(req, !req.completed, ctx, { pay: true }), updatedAt: ctx.now };
        })));
      },

      updateRequirementProgress: (abilityId, skillId, requirementId, progress) => {
        setAndLogSkillXp(set, get, (state) => editRequirements(state, abilityId, skillId, (requirements, ctx) => mapSame(requirements, req => {
          if (req.id !== requirementId) return req;
          const value = Math.min(100, Math.max(0, progress));
          // RO-D3: a linked source that regresses never un-completes finished work.
          if (req.completed && isLinked(req) && value < 100) return req;
          const completed = value >= 100;
          if (completed === req.completed && value === req.progress) return req;
          if (completed || req.completed) {
            return { ...setRequirementCompleted(req, completed, ctx, { pay: completed, progress: value }), updatedAt: ctx.now };
          }
          return { ...req, progress: value, updatedAt: ctx.now };
        })));
      },

      submitRequirementOutput: (abilityId, skillId, requirementId, outputContent, outputFile) => {
        setAndLogSkillXp(set, get, (state) => editRequirements(state, abilityId, skillId, (requirements, ctx) => mapSame(requirements, req => {
          // Only output requirements take an output.
          if (req.id !== requirementId || req.requirementType !== 'output') return req;
          // Waiting for review: not complete yet (50%), and nothing is paid until it is.
          const completed = !req.reviewRequired;
          const next = setRequirementCompleted(
            { ...req, outputContent, outputFile, reviewStatus: req.reviewRequired ? 'pending' : undefined },
            completed, ctx, { pay: completed, progress: req.reviewRequired ? 50 : 100 },
          );
          return { ...next, updatedAt: ctx.now };
        })));
      },
      
      // Getters
      getAbilityById: (id) => {
        return get().abilities.find(ability => ability.id === id);
      },
      
      getSkillById: (abilityId, skillId) => {
        const ability = get().abilities.find(a => a.id === abilityId);
        if (!ability) return undefined;
        
        return ability.skills.find(s => s.id === skillId);
      },
      
      getRequirementById: (abilityId, skillId, requirementId) => {
        const ability = get().abilities.find(a => a.id === abilityId);
        if (!ability) return undefined;
        
        const skill = ability.skills.find(s => s.id === skillId);
        if (!skill) return undefined;
        
        return skill.requirements.find(r => r.id === requirementId);
      }
    }),
    {
      name: 'abilities-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 7,
      // initializedUserId stays transient so load-on-login runs each start.
      partialize: (state: AbilitiesState) => ({
        abilities: state.abilities,
        folders: state.folders,
        // Must persist: a tombstone that dies on reload lets the next merge
        // resurrect the record it was suppressing.
        deleted: state.deleted,
        userLevel: state.userLevel,
        domainLevels: state.domainLevels,
        cachedUserId: state.cachedUserId,
      }) as any,
      migrate: (persistedState: any, version: number) => {
        const state = persistedState as AbilitiesState;

        // Migrate from version 1 to version 2
        if (version < 2) {
          // Add completionStartDate to existing requirements with links
          if (state.abilities) {
            state.abilities = state.abilities.map(ability => ({
              ...ability,
              skills: ability.skills.map(skill => ({
                ...skill,
                requirements: skill.requirements.map(req => {
                  // If requirement has a link but no completionStartDate, set it to createdAt
                  if (req.linkType && req.linkId && !req.completionStartDate) {
                    return {
                      ...req,
                      completionStartDate: req.createdAt,
                      currentCompletions: 0
                    };
                  }
                  return req;
                })
              }))
            }));
          }
        }

        // Migrate from version 2 to version 3: Full XP Reset
        if (version < 3) {
          if (state.abilities) {
            state.abilities = state.abilities.map(ability => ({
              ...ability,
              level: 1,
              xp: 0,
              progress: 0,
              skills: ability.skills.map(skill => ({
                ...skill,
                xp: 0,
                progress: 0,
                completedAt: undefined,
                completionBonusAwarded: false,
                requirements: skill.requirements.map(req => ({
                  ...req,
                  xp: 0,
                  completed: false,
                  progress: 0
                }))
              }))
            }));
          }
        }

        // Migrate from version 3 to version 4: Add color variation method
        if (version < 4) {
          if (state.abilities) {
            state.abilities = state.abilities.map(ability => ({
              ...ability,
              colorVariationMethod: ability.colorVariationMethod || 'tint-shade'
            }));
          }
        }

        // Migrate from version 4 to version 5: Add folders and order
        if (version < 5) {
          // Initialize folders array if it doesn't exist
          if (!state.folders) {
            state.folders = [];
          }

          // Add order field to existing abilities
          if (state.abilities) {
            state.abilities = state.abilities.map((ability, index) => ({
              ...ability,
              order: ability.order !== undefined ? ability.order : index,
              folderId: ability.folderId || undefined
            }));
          }
        }

        // Migrate from version 5 to version 6: Add domain-based level cap system
        if (version < 6) {
          // Initialize domainLevels if it doesn't exist
          if (!state.domainLevels) {
            state.domainLevels = {};
          }

          // Add new cap-related fields to existing abilities
          if (state.abilities) {
            state.abilities = state.abilities.map(ability => ({
              ...ability,
              excessXP: ability.excessXP ?? 0,
              totalXPEarned: ability.totalXPEarned ?? ability.xp ?? 0,
              isCapped: ability.isCapped ?? false,
              lastDomainLevelCheck: ability.lastDomainLevelCheck ?? 0
            }));
          }
        }

        // Version 6 → 7 (RO-A): requirements keep an earned record; skills keep
        // their bonus. RECORDS ONLY — no level or cache is recomputed on load.
        // ⚠️⚠️ NO NEW STAMP HERE (review finding 1). This runs on the LOCAL cache
        // before initialize merges it with the cloud; stamping would make a week-old
        // cache "newer" than another device's edits and than cloud tombstones, so the
        // stale copy would win and resurrect deletions. initialize() and the blob
        // build convert again AFTER merging, where stamping the winner is safe.
        if (version < 7 && state.abilities) {
          state.abilities = convertLegacyAbilities(state.abilities, {
            ...conversionContext(state.userLevel),
            touch: ability => ability.updatedAt,
          });
        }

        return state;
      },
      // NOTE: default abilities are intentionally NOT seeded here. The old
      // onRehydrateStorage seed ran on EVERY load where local storage was empty
      // (cleared cache, new browser, a second device) and minted fresh random
      // ids each time, which the id-based merge then accumulated into the cloud blob —
      // the duplicate-abilities bug. Seeding now happens once in initialize(),
      // only when the account has no cloud row, using stable ids.
    }
  )
);

// Auto-sync to Supabase whenever abilities/folders change (covers all
// mutations). Ref-equality skips non-data changes (userLevel, domainLevels, and
// derived cap recalcs that return the same abilities ref). No suppression flag:
// initialize() reuses references on a no-op merge, and a write-back when it DID
// change something is what makes the two sides converge.
useAbilitiesStore.subscribe((state, prev) => {
  if (swappingAccount()) return; // a change of account swaps the saved copy in — not an edit
  if (
    state.abilities === prev.abilities &&
    state.folders === prev.folders &&
    state.deleted === prev.deleted
  ) return;
  abilitySync.schedule();
});

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: an account's edits whose
   write had not landed lived only until the next account's load replaced the copy, and the next
   account's edits made before its own load were merged into the last one's row by ITS next load. */
scopeToAccount(useAbilitiesStore);
